import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useNoIndex } from "@/components/accordi/accordiUtils";
import { Loader2, LogOut, ShieldCheck } from "lucide-react";

const KPI = ["accessi_landing", "accessi_area", "preventivi_generati", "preventivi_salvati", "richieste_consulenza", "documentazione_caricata", "proposte_emesse", "polizze_attivate", "referral_ricevuti"];
const TASSI = [["conv_area_preventivo", "Dall'area al preventivo salvato"], ["conv_preventivo_polizza", "Dal preventivo alla polizza"], ["conv_landing_polizza", "Dalla landing alla polizza"]];

// Dashboard referente: solo numeri aggregati del proprio accordo.
export default function ReferenteDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [per, setPer] = useState({ da: "", a: "" });
  useNoIndex();
  useEffect(() => {
    api.get(`/referente/dashboard?${new URLSearchParams(per)}`).then(({ data }) => setD(data)).catch(() => navigate("/referente", { replace: true }));
  }, [per]); // eslint-disable-line
  const esci = async () => { await api.post("/referente/logout"); navigate("/referente"); };
  if (!d) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const m = d.metriche;
  const max = Math.max(1, ...d.andamento.map((x) => x.accessi_area || 0));

  return (
    <div className="min-h-screen bg-bone" data-testid="referente-dashboard">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-4">
          <img src="/assets/assietica-logo.png" alt="Assietica" className="h-9" />
          <div className="min-w-0 flex-1"><div className="truncate font-heading text-lg font-semibold text-forest" data-testid="referente-accordo">{d.accordo.denominazione}</div><div className="text-xs text-muted-foreground">Accordo {d.accordo.codice} · {d.accordo.stato} · scadenza {new Date(d.accordo.scadenza).toLocaleDateString("it-IT")}</div></div>
          <Button variant="ghost" size="sm" onClick={esci} data-testid="referente-esci" className="gap-1.5"><LogOut className="h-4 w-4" />Esci</Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-8 px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><h1 className="font-heading text-3xl font-semibold text-forest">Andamento dell'accordo</h1><p className="flex items-center gap-1.5 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4" />Solo dati aggregati: nessun nominativo, recapito o dato di polizza (GDPR).</p></div>
          <div className="flex gap-2">
            <Input type="date" value={per.da} onChange={(e) => setPer({ ...per, da: e.target.value })} data-testid="referente-da" className="w-40" />
            <Input type="date" value={per.a} onChange={(e) => setPer({ ...per, a: e.target.value })} data-testid="referente-a" className="w-40" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {KPI.map((k) => <Card key={k} className="rounded-xl p-4"><div className="text-xs text-muted-foreground">{d.labels[k]}</div><div className="font-heading text-3xl font-semibold text-forest" data-testid={`ref-kpi-${k}`}>{m[k]}</div></Card>)}
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {TASSI.map(([k, l]) => <Card key={k} className="rounded-xl border-terracotta/30 bg-terracotta/5 p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="font-heading text-3xl font-semibold text-terracotta" data-testid={`ref-tasso-${k}`}>{m[k]}%</div></Card>)}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="space-y-3 rounded-xl p-5">
            <h2 className="font-heading text-lg font-semibold text-forest">Per prodotto</h2>
            {d.per_prodotto.length === 0 ? <p className="text-sm text-muted-foreground">Ancora nessun preventivo.</p> : d.per_prodotto.map((r) => (
              <div key={r.product} className="flex justify-between border-b pb-2 text-sm" data-testid={`ref-prodotto-${r.product}`}><span className="text-forest">{r.label}</span><span className="text-muted-foreground">{r.preventivi_generati || 0} generati · {r.preventivi_salvati || 0} salvati · {r.polizze_attivate || 0} polizze</span></div>
            ))}
          </Card>
          <Card className="space-y-3 rounded-xl p-5">
            <h2 className="font-heading text-lg font-semibold text-forest">Accessi all'area per mese</h2>
            {d.andamento.length === 0 ? <p className="text-sm text-muted-foreground">Nessun dato nel periodo.</p> : d.andamento.map((x) => (
              <div key={x.mese} className="flex items-center gap-3 text-sm"><span className="w-16 text-muted-foreground">{x.mese}</span><div className="h-3 rounded-full bg-forest" style={{ width: `${(100 * (x.accessi_area || 0)) / max}%`, minWidth: 4 }} /><span className="tabular-nums text-forest">{x.accessi_area || 0}</span></div>
            ))}
          </Card>
        </div>
      </main>
    </div>
  );
}
